/** How amenities are grouped on the Amenities step. Amenities come from the API by name; any name not listed here lands in "More amenities". */
export const AMENITY_GROUPS: { group: string; items: string[] }[] = [
  {
    group: 'Essentials & climate',
    items: ['Wi-Fi', 'AC', 'Power Backup', 'Hot Bath Water', 'Wardrobe(s)', 'Extra linens, pillows'],
  },
  {
    group: 'Kitchen & dining',
    items: [
      'Kitchen',
      'Fridge',
      'Microwave',
      'Gas Stove',
      'Induction Stove',
      'Dishwasher',
      'Cooking Utensils',
      'Basic Cutlery',
      'Dining Table',
      'Kettle',
      'Toaster',
      'Blender',
    ],
  },
  {
    group: 'Bathroom, linen & laundry',
    items: [
      'Bathtub',
      'Towels',
      'Body wash, hand wash, soap',
      'Basic Toiletries',
      'Hair dryer',
      'Washing Machine',
      'Iron',
      'Laundry Service',
    ],
  },
  {
    group: 'Pools, wellness & outdoors',
    items: [
      'Pvt Pool',
      'Shared Pool',
      'Heated Pool',
      'Jacuzzi',
      'Pvt Garden',
      'Shared Garden',
      'Barbeque Grill',
      'Gym',
    ],
  },
  {
    group: 'Work, entertainment & kids',
    items: ['Workspace', 'Television', 'Indoor Games', 'Kids Play Area'],
  },
  {
    group: 'Parking & access',
    items: ['Pvt Car Park', 'Shared Car Park', 'EV Charging', 'Private Lift', 'Shared Lift'],
  },
  {
    group: 'Services & hospitality',
    items: [
      'Caretaker',
      '24/7 Caretaker',
      'Chef On Call',
      'House Keeping',
      'Flexi Checkin/Out',
      'Welcome Basket',
    ],
  },
  {
    group: 'Safety & security',
    items: ['First Aid Kit', 'Fire Extinguisher', 'Fire Extenguisher', 'Fire Alarm'],
  },
];

/** Backend names with typos, shown with the correct spelling. */
const LABEL_FIXES: Record<string, string> = {
  fireextenguisher: 'Fire Extinguisher',
};

export const amenityLabel = (name: string): string =>
  LABEL_FIXES[name.toLowerCase().replace(/[^a-z0-9]/g, '')] ?? name;

export const OTHER_GROUP = 'More amenities';

const norm = (name: string): string => name.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Splits API amenities into the groups above, keeping the group order and the order within each group. */
export function groupAmenities<T extends { name: string }>(
  amenities: T[],
): { group: string; items: T[] }[] {
  const byName = new Map(amenities.map((a) => [norm(a.name), a]));
  const used = new Set<string>();
  const grouped = AMENITY_GROUPS.map(({ group, items }) => ({
    group,
    items: items
      .map((label) => {
        const found = byName.get(norm(label));
        if (found) used.add(norm(label));
        return found;
      })
      .filter((a): a is T => !!a),
  }));
  const rest = amenities.filter((a) => !used.has(norm(a.name)));
  if (rest.length > 0) grouped.push({ group: OTHER_GROUP, items: rest });
  return grouped.filter((g) => g.items.length > 0);
}
